export type WorkspaceTargetTab = 'dashboard' | 'notifications' | 'permissionManagement' | string
export type ModuleNotificationType = 'INFO' | 'SUCCESS' | 'WARNING' | 'TASK'

/** 微前端与工作台导航目标 */
export interface ModuleShellTarget {
  tab?: WorkspaceTargetTab
  permissionSection?: string
  moduleCode?: string
  menuCode?: string
  path?: string
  /** 目标 MFE 内部视图，如 transactions / share / config */
  view?: string
  /** 支付业务模块编码，如 ticketing；不是租户断言 */
  paymentModule?: string
  mchOrderNo?: string
  payOrderNo?: string
  planId?: string
  /** 允许的返回菜单号，禁止任意 URL */
  returnMenuCode?: string
}

const LOCATE_TOKEN = /^[A-Za-z0-9._:-]+$/

function sanitizeLocateToken(value: unknown, max: number): string | undefined {
  if (typeof value !== 'string') return undefined
  const token = value.trim()
  if (!token || token.length > max || !LOCATE_TOKEN.test(token)) return undefined
  return token
}

export type ModuleLocateFields = Pick<
  ModuleShellTarget,
  'view' | 'paymentModule' | 'mchOrderNo' | 'payOrderNo' | 'planId' | 'returnMenuCode'
>

/** 白名单序列化业务定位字段；不含 tenantId、密钥或回跳 URL。 */
export function pickModuleLocateTarget(
  source: Partial<ModuleShellTarget> | null | undefined
): ModuleLocateFields | undefined {
  if (!source) return undefined
  const view = sanitizeLocateToken(source.view, 32)
  const paymentModule = sanitizeLocateToken(source.paymentModule, 64)
  const mchOrderNo = sanitizeLocateToken(source.mchOrderNo, 128)
  const payOrderNo = sanitizeLocateToken(source.payOrderNo, 128)
  const planId = sanitizeLocateToken(source.planId, 64)
  const returnMenuCode = sanitizeLocateToken(source.returnMenuCode, 96)
  if (!view && !paymentModule && !mchOrderNo && !payOrderNo && !planId && !returnMenuCode) return undefined
  return { view, paymentModule, mchOrderNo, payOrderNo, planId, returnMenuCode }
}

/** 模块通知载荷 */
export interface ModuleNotificationPayload {
  title: string
  body?: string
  type?: ModuleNotificationType
  sender?: string
  moduleCode?: string
  moduleName?: string
  target?: ModuleShellTarget
  linkText?: string
  createdAt?: string
}

/** SaaS 通知目标 */
export interface SaasNotificationTarget extends ModuleShellTarget {}

/** SaaS 通知记录 */
export interface SaasNotification {
  id: number
  tenantId: number
  userId: number
  title: string
  body: string
  type: ModuleNotificationType
  sender: string
  moduleCode: string
  moduleName: string
  target: SaasNotificationTarget
  linkText: string
  read: boolean
  createdAt: string
}

export interface CreateSaasNotificationRequest extends Omit<ModuleNotificationPayload, 'createdAt'> {}

/** 微前端与宿主壳子通信桥梁接口 */
export interface ModuleShellBridge {
  notify: (payload: ModuleNotificationPayload) => void
  open: (target: ModuleShellTarget) => void
  register: (registration: ModuleShellRegistration) => void
}

export interface ModuleShellMessage {
  source: typeof MODULE_SHELL_MESSAGE_SOURCE
  type: 'notify' | 'open' | 'register'
  payload?: ModuleNotificationPayload
  target?: ModuleShellTarget
  registration?: ModuleShellRegistration
}

export interface ModuleShellDefaults {
  moduleCode: string
  moduleName: string
  sender?: string
  target?: ModuleShellTarget
}

export interface ModuleShellRegistration extends ModuleShellDefaults {}

export const MODULE_SHELL_MESSAGE_SOURCE = 'xjlv2-module'

/** 规范化模块通知参数 */
export function normalizeModuleNotification(
  payload: ModuleNotificationPayload,
  defaults: ModuleShellDefaults
): ModuleNotificationPayload {
  return {
    moduleCode: defaults.moduleCode,
    moduleName: defaults.moduleName,
    sender: defaults.sender ?? defaults.moduleName,
    target: defaults.target,
    ...payload
  }
}

/** 创建宿主通信桥梁（具备 SSR / Node 安全环境守卫） */
export function createModuleShellBridgeFallback(
  targetWindow?: Pick<Window, 'postMessage'>
): ModuleShellBridge {
  const win = targetWindow ?? (typeof window !== 'undefined' ? window.parent : undefined)
  return {
    notify: (payload) => {
      win?.postMessage(
        {
          source: MODULE_SHELL_MESSAGE_SOURCE,
          type: 'notify',
          payload
        } satisfies ModuleShellMessage,
        '*'
      )
    },
    open: (target) => {
      win?.postMessage(
        {
          source: MODULE_SHELL_MESSAGE_SOURCE,
          type: 'open',
          target
        } satisfies ModuleShellMessage,
        '*'
      )
    },
    register: (registration) => {
      win?.postMessage(
        {
          source: MODULE_SHELL_MESSAGE_SOURCE,
          type: 'register',
          registration
        } satisfies ModuleShellMessage,
        '*'
      )
    }
  }
}
